/**
 * "What you can use" — Section A on /prompts.
 *
 * D-159 + Ean 2026-06-10 acceptance criteria: always-visible section that
 * mixes prompts an admin assigned the user (visibility='account_installed')
 * with prompts the user created themselves (user_private | user_shared |
 * account_custom). The VISIBILITY_LABEL pill on each tile tells you which is
 * which. Renders empty-state when the user has nothing assigned + nothing
 * authored yet, with a dual CTA to write-your-own or browse the market.
 *
 * Per Ean 2026-06-10 card-density: collapse to 6 tiles with Show-all toggle.
 */

import { Button } from '@olytic/foundation';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

import type { KnowledgePromptInstance } from '../../lib/api.ts';
import { EmptyState } from '../../components/EmptyState.tsx';
import { Pill } from '../../components/StatusPill.tsx';
import { PromptCategoryBadge } from '../../components/PromptCategoryBadge.tsx';

import { VISIBILITY_LABEL } from './visibility-labels.ts';

interface MyPromptsSectionProps {
  list: KnowledgePromptInstance[];
  /** Opens the inline create form on the parent. */
  onCreate: () => void;
}

// Phase 2 Block 2A Cluster B (2026-06-30): legacy slugFor preserved for any
// remaining callers but the canonical nav is now `p.short_id` directly.
// Block 2D close will delete slugFor entirely once all callers are updated.
function slugFor(p: KnowledgePromptInstance): string {
  return p.short_id;
}

export function MyPromptsSection({ list, onCreate }: MyPromptsSectionProps) {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  return (
    <section className="catalog-section">
      <div className="catalog-section-heading">
        <h2 className="catalog-section-title">
          What you can use <span className="catalog-section-count">({list.length})</span>
        </h2>
        <p className="catalog-section-helper">
          Prompts your admin assigned you, plus the ones you've made yourself. The tag tells you which is which.
        </p>
        {list.length > 6 && (
          <Button type="button" variant="link" onClick={() => setShowAll((v) => !v)} style={{ fontSize: 12, marginTop: 4 }}>
            {showAll ? 'Show fewer' : `Show all (${list.length})`}
          </Button>
        )}
      </div>
      {list.length === 0 ? (
        // Push 5-F (2026-07-11): migrate legacy `cta` -> structured primary+secondary.
        <EmptyState
          kind="prompts"
          title="No prompts ready for you yet"
          body="Write your first prompt, or browse the market for a ready-made one."
          primaryAction={{ label: 'Write your own', onClick: onCreate }}
          secondaryAction={{ label: 'Browse the market', to: '/market?tab=prompts' }}
        />
      ) : (
        <div className="tile-grid">
          {(showAll ? list : list.slice(0, 6)).map((p) => (
            <button
              key={p.id}
              type="button"
              className="tile"
              onClick={() => navigate(`/prompts/${slugFor(p)}`)}
            >
              <div className="tile-header">
                {/* Push #22 rule 3: category-color badge replaces the
                    vendor-logo slot prompts don't have. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <PromptCategoryBadge category={p.category ?? null} size="xs" />
                  </div>
                  <p className="tile-name">{p.display_name}</p>
                </div>
              </div>
              {p.description !== null && p.description !== '' && (
                <p className="tile-tagline">{p.description}</p>
              )}
              <div className="tile-footer">
                <Pill kind="info">{VISIBILITY_LABEL[p.visibility]}</Pill>
                <span className="muted" style={{ fontSize: '12px' }}>Open →</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
